import { IonIcon } from "@ionic/react";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { briefcaseOutline, closeOutline, ellipsisHorizontal, timeOutline } from "ionicons/icons";
import {
  APPLICATION_STATUS_DISPLAY,
  UNKNOWN_STATUS_DISPLAY,
} from "@/lib/application/status-display";
import type { Application } from "@/lib/backend/api";
import { formatDateBox, formatTimeRange } from "@/utils/format-datetime";
import { getSlotRoleLabel } from "@/utils/functions/get-slot-role-label";

interface ApplicationListItemProps {
  /** Application data */
  application: Application;
  /** Whether the three dots menu should be shown */
  showMenu: boolean;
  /** Callback when revoke is requested */
  onRevokeClick: () => void;
}

/** Single application item in the list. Shows date, project details, and optional menu. */
export function ApplicationListItem({
  application,
  showMenu,
  onRevokeClick,
}: ApplicationListItemProps) {
  const { t } = useTranslation();
  const [openPopover, setOpenPopover] = useState(false);

  // Een status die de api later toevoegt heeft hier geen regel: dan blijft de ruwe waarde
  // staan, net als in de oude `default:`-tak.
  const statusDisplay = APPLICATION_STATUS_DISPLAY[application.status];
  const statusLabel = statusDisplay ? t(statusDisplay.labelKey) : application.status;
  const statusIcon = statusDisplay?.icon ?? UNKNOWN_STATUS_DISPLAY.icon;
  const statusColor = statusDisplay?.color ?? UNKNOWN_STATUS_DISPLAY.color;

  // Projecttijd. `tzDate()` leek dit al te regelen, maar levert een Date waarvan
  // de LOKALE velden de Amsterdamse tijd voorstellen — en tempo's `format()` legt
  // daar de tijdzone van het toestel nóg eens overheen. Op een telefoon in
  // Toronto werd 15:00 daardoor 03:00.
  const timeZone = application.availabilitySlot?.project?.timeZone;
  const dateBox = formatDateBox(application.startAt, { timeZone });

  // Get project/shift details from availability slot
  const slot = application.availabilitySlot;
  const projectName = slot?.project?.name || "Project";
  // Position name (e.g. "Bardienst") wins over the generic function name (#373).
  const userFunctionName = getSlotRoleLabel(slot, "Functie");

  return (
    <div className="relative flex gap-5 border-b border-bordercolor-primary pb-5">
      {/* Date box */}
      <div className="flex w-12 shrink-0 flex-col items-center px-1 py-2">
        <div className="flex flex-col items-center pb-0.5 text-center text-txt-primary">
          <p className="text-sm font-semibold leading-tight">{dateBox.month}</p>
          <p className="text-2xl font-semibold leading-none">{dateBox.day}</p>
        </div>
        <p className="text-sm font-medium text-txt-tertiary">{dateBox.caption}</p>
      </div>

      {/* Application details */}
      <div className="flex flex-1 flex-col gap-3 py-2">
        <div className="flex flex-col gap-2">
          {/* Title */}
          <h3 className="overflow-hidden text-ellipsis text-lg font-semibold leading-[1.6] text-txt-primary">
            {projectName}
          </h3>

          {/* Metadata */}
          <div className="flex flex-col gap-2">
            {/* Function */}
            <div className="flex items-center gap-1.5">
              <IonIcon icon={briefcaseOutline} className="text-xl text-txt-tertiary" />
              <p className="text-base font-medium text-txt-tertiary">{userFunctionName}</p>
            </div>

            {/* Time */}
            <div className="flex items-center gap-1.5">
              <IonIcon icon={timeOutline} className="text-base text-txt-tertiary" />
              <p className="text-base font-medium text-txt-tertiary">
                {formatTimeRange(application.startAt, application.endAt, { timeZone })}
              </p>
            </div>

            {/* Status */}
            <div className="flex items-center justify-start gap-1.5">
              <IonIcon icon={statusIcon} className={`text-base ${statusColor}`} />
              <p className={`text-base font-medium ${statusColor}`}>{statusLabel}</p>
            </div>
          </div>
        </div>
      </div>

      {/* Three dots menu - only show if application can be revoked */}
      {showMenu && (
        <div className="relative flex shrink-0 items-start py-2">
          <button
            type="button"
            onClick={(e) => {
              e.stopPropagation();
              setOpenPopover(!openPopover);
            }}
            className="flex h-10 w-10 items-center justify-center rounded-full hover:bg-surface-secondary"
          >
            <IonIcon icon={ellipsisHorizontal} className="text-2xl text-txt-primary" />
          </button>

          {/* Popover */}
          {openPopover && (
            <>
              {/* Backdrop to close popover when clicking outside */}
              <div className="fixed inset-0 z-10" onClick={() => setOpenPopover(false)} />

              {/* Popover content */}
              <div className="absolute right-0 top-12 z-20 w-max rounded-xl border border-bordercolor-primary bg-white px-5 py-3.5 shadow-[0px_20px_33px_8px_rgba(0,0,0,0.08),0px_10px_18px_8px_rgba(0,0,0,0.02)]">
                <button
                  type="button"
                  onClick={(e) => {
                    e.stopPropagation();
                    setOpenPopover(false);
                    onRevokeClick();
                  }}
                  className="flex items-center justify-between gap-4 whitespace-nowrap"
                >
                  <span className="text-txt-error text-base font-medium">Aanmelding intrekken</span>
                  <IonIcon icon={closeOutline} className="text-txt-error text-xl" />
                </button>
              </div>
            </>
          )}
        </div>
      )}
    </div>
  );
}
